import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell, UserCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthSession } from "@/hooks/useAuthSession";

interface PendingRequest {
  id: string;
  message: string | null;
  requested_at: string | null;
  coaches: {
    coach_name: string;
  } | null;
}

export const PendingSupervisionDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const { user, isAuthenticated } = useAuthSession();
  const { t } = useTranslation('supervision');
  const navigate = useNavigate();

  useEffect(() => {
    const checkPendingRequests = async () => {
      if (!user?.id || !isAuthenticated) return;

      try {
        const { data, error } = await supabase
          .from('supervision_requests')
          .select(`
            id,
            message,
            requested_at,
            coaches (
              coach_name
            )
          `)
          .eq('trainee_user_id', user.id)
          .eq('status', 'pending')
          .gt('expires_at', new Date().toISOString());

        if (error) {
          console.error('Error checking pending requests:', error);
          return;
        }

        if (data && data.length > 0) {
          setPendingRequests(data);
          setIsOpen(true);
        }
      } catch (error) {
        console.error('Error in pending supervision check:', error);
      }
    };

    // Small delay to ensure auth is fully loaded
    const timer = setTimeout(checkPendingRequests, 1000);
    return () => clearTimeout(timer);
  }, [user?.id, isAuthenticated]);

  const handleViewRequests = () => {
    setIsOpen(false);
    navigate('/supervision-management');
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (pendingRequests.length === 0) return null;

  const count = pendingRequests.length;
  const firstCoachName = pendingRequests[0]?.coaches?.coach_name;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-full">
              <Bell className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-xl">
              {count === 1 
                ? t('notifications.pendingTitle')
                : t('notifications.pendingTitlePlural', { count })}
            </DialogTitle>
          </div>
          <DialogDescription className="text-base pt-2">
            {count === 1 
              ? t('notifications.pendingDescriptionSingle', { coachName: firstCoachName || t('coach.unknownCoach') })
              : t('notifications.pendingDescriptionMultiple', { count })}
          </DialogDescription>
        </DialogHeader>

        {count === 1 && pendingRequests[0]?.message && (
          <div className="bg-muted p-3 rounded-lg">
            <p className="text-sm text-muted-foreground italic">
              "{pendingRequests[0].message}"
            </p>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <UserCheck className="h-4 w-4" />
          <span>
            {t('notifications.actionHint')}
          </span>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto">
            {t('notifications.later')}
          </Button>
          <Button onClick={handleViewRequests} className="w-full sm:w-auto">
            {t('notifications.viewRequests')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
